import { Fade, ImageListItem, ImageListItemBar } from "@mui/material";
import React, { useEffect, useRef } from "react";
import NoImageView from "./NoImageView";
import ItemViewChips from "./ItemViewChips";
import CheckIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/Block';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const MemoItem = React.memo(Item);

// A touch only counts as a tap when the finger stayed put, the page did not
// move under it, and momentum scrolling had already settled. Taps that land
// while the list is still gliding are the user stopping the scroll, not asking
// to open a listing.
const TAP_MOVE_THRESHOLD = 10;
const TAP_SCROLL_THRESHOLD = 2;
const SCROLL_SETTLE_MS = 250;
let lastScrollTime = 0;
window.addEventListener("scroll", () => {
    lastScrollTime = Date.now();
}, { passive: true });

const visibilityCallbacks = new Map();
const itemVisibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const onVisible = visibilityCallbacks.get(entry.target);
        itemVisibilityObserver.unobserve(entry.target);
        visibilityCallbacks.delete(entry.target);
        onVisible?.();
    });
}, { rootMargin: "1000px" });

function observeUntilVisible(element, onVisible) {
    visibilityCallbacks.set(element, onVisible);
    itemVisibilityObserver.observe(element);

    const bounds = element.getBoundingClientRect();
    if (bounds.bottom >= -1000 && bounds.top <= window.innerHeight + 1000) {
        itemVisibilityObserver.unobserve(element);
        visibilityCallbacks.delete(element);
        onVisible();
    }

    return () => {
        itemVisibilityObserver.unobserve(element);
        visibilityCallbacks.delete(element);
    };
}

function Item({
    alwaysShow,
    item,
    handleToggle,
    setLoading,
    isFirst,
    isCurrentOverlayId,
    setCurrentOverlayId,
    sort,
    handlePreLoad,
    preload,
    selectedImageUrl,
    isClicked, isFilterSeller, isFirstItemRefresh, firstItemPositionTop
}) {
    const [firstItemPositionTopCached, setFirstItemPositionTopCached] = React.useState(firstItemPositionTop);
    const [localLoading, setLocalLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [hoverOverlay, setHoverOverlay] = React.useState(false);
    const [zoom, setZoom] = React.useState(false);
    const [clicked, setClicked] = React.useState(isClicked);
    const [preloadedImageUrl, setPreloadedImageUrl] = React.useState(null);
    const [coverByWidth, setCoverByWidth] = React.useState(true);
    let url = item.thumbnailImages?.[0]?.imageUrl ?? item.image?.imageUrl;
    const pictureUrl = url?.slice(0, url?.lastIndexOf('/')) + '/s-l960.jpg';
    let itemTitle = item.title;
    useEffect(() => {
        if (error || pictureUrl === '/s-l960.jpg') {
            setLocalLoading(false);
            if (isFirst) {
                setLoading(false);
            }
        }
    }, [pictureUrl, error])

    useEffect(() => {
        if (isFirstItemRefresh) {
            setFirstItemPositionTopCached(firstItemPositionTop);
        }
    }, [isFirstItemRefresh, firstItemPositionTop])

    useEffect(() => {
        setClicked(isClicked);
    }, [isClicked]);

    function toggleOverlay(enter) {
        setHoverOverlay(enter);
    }

    useEffect(() => {
        if ((pictureUrl === '/s-l960.jpg' || error) && preload?.id === item.legacyItemId) {
            setPreloadedImageUrl(preload.image?.imageUrl);
            setError(false);
        }
    }, [preload]);

    const myRef = useRef();
    const pointerStart = useRef(null);
    const [inViewport, setInViewport] = React.useState(false);
    useEffect(() => {
        if (inViewport || !myRef.current) return;
        return observeUntilVisible(myRef.current, () => setInViewport(true));
    }, [inViewport]);
    const isInteractiveOverlay = hoverOverlay || isCurrentOverlayId;
    const showOverlay = isInteractiveOverlay || (inViewport && alwaysShow)
    const overlayZoom = (inViewport && alwaysShow) ? zoom : (isInteractiveOverlay || zoom);
    const showStatusShade = (clicked || isFilterSeller) && !overlayZoom;
    const showError = (pictureUrl === ('/s-l960.jpg' && error) || error);

    return (<>
        <ImageListItem
            className={`${localLoading && !showError && !isFilterSeller ? "CustomShimmer " : ""}ListingGridItem`}
            id={isFirstItemRefresh ? "FirstItem" : undefined}
            sx={{ overflow: "hidden", cursor: "pointer" }}
            ref={myRef}
            onPointerDown={(e) => {
                pointerStart.current = e.pointerType === 'touch' ? {
                    x: e.clientX,
                    y: e.clientY,
                    scrollY: window.scrollY,
                    scrollSettling: Date.now() - lastScrollTime < SCROLL_SETTLE_MS
                } : null;
            }}
            onPointerCancel={() => {
                pointerStart.current = null;
            }}
            onPointerUp={(e) => {
                const start = pointerStart.current;
                pointerStart.current = null;
                if (e.pointerType === 'touch') {
                    if (!start) return;
                    const movedFar = Math.abs(e.clientX - start.x) > TAP_MOVE_THRESHOLD ||
                        Math.abs(e.clientY - start.y) > TAP_MOVE_THRESHOLD;
                    const pageScrolled = Math.abs(window.scrollY - start.scrollY) > TAP_SCROLL_THRESHOLD;
                    if (movedFar || pageScrolled) return;
                    if (!showOverlay) {
                        setCurrentOverlayId(item.itemId);
                        if (!myRef.current?.classList.contains("CurrentListingGridItem")) handlePreLoad(item.legacyItemId);
                        return;
                    }
                    if (start.scrollSettling) return;
                }
                if (e.button === 2) {
                    setCurrentOverlayId(item.itemId);
                    return;
                }
                const transitionSource = e.currentTarget.querySelector(".ImageItem") ?? e.currentTarget;
                handleToggle(2, item, undefined, transitionSource);
                setTimeout(() => {
                    setCurrentOverlayId(0);
                }, 10);
            }}
            onPointerEnter={(e) => {
                if (e.pointerType !== 'touch' && !(inViewport && alwaysShow)) {
                    toggleOverlay(true);
                } else if (e.pointerType !== 'touch' && alwaysShow) {
                    setZoom(true);
                }
            }}
            onPointerLeave={(e) => {
                if (e.pointerType !== 'touch' && !(inViewport && alwaysShow)) {
                    toggleOverlay(false);
                } else if (e.pointerType !== 'touch' && alwaysShow) {
                    setZoom(false);
                }
            }}>
            <div className="ListingGridItemContent">
            {showError ?
                <NoImageView itemTitle={itemTitle} />
                : <Fade in={!localLoading} key={item.itemId}>
                    {!inViewport ? <div className={"ImageSize"} style={{ width: "100%" }} /> :
                        <div className="ImageSize ListingImageViewport">
                            <img className={"ImageItem"} style={{
                                width: coverByWidth ? "100%" : "auto",
                                height: coverByWidth ? "auto" : "100%",
                                transform: `translate(-50%, -50%)${overlayZoom ? " scale(1.2)" : ""}`
                            }}
                                src={selectedImageUrl ?? preloadedImageUrl ?? `${pictureUrl}`}
                                decoding="async"
                                alt={item.title}
                                onLoad={(event) => {
                                    const image = event.currentTarget;
                                    const viewport = image.parentElement;
                                    if (image.naturalHeight && viewport?.clientHeight) {
                                        setCoverByWidth(
                                            image.naturalWidth / image.naturalHeight <=
                                            viewport.clientWidth / viewport.clientHeight
                                        );
                                    }
                                    setLocalLoading(false);
                                    if (isFirst) {
                                        setLoading(false);
                                    }
                                }}
                                onError={() => {
                                    setLocalLoading(false);
                                    setError(true);
                                    if (isFirst) {
                                        setLoading(false);
                                    }
                                }}
                            />
                        </div>}
                </Fade>
            }
            <Fade in={showOverlay} key={item.itemId + "overlay-top"} unmountOnExit>
                <ImageListItemBar
                    className="ListingOverlayTop"
                    sx={{
                        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0))',
                        paddingBottom: '1em'
                    }}
                    position={'top'}
                    subtitle={<>
                        <div style={{
                            userSelect: 'none',
                            paddingLeft: '1em',
                            paddingRight: '1em',
                            paddingTop: '1em',
                            overflow: 'hidden',
                            lineHeight: '1.25em',
                            textShadow: "0 0 2px rgba(0,0,0,.5)",
                            marginRight: (clicked || isFilterSeller) ? "50px" : undefined,
                            display: "-webkit-box",
                            whiteSpace: "normal",
                            WebkitLineClamp: "3",
                            WebkitBoxOrient: "vertical",
                        }}>
                            {item.title}
                        </div>
                    </>}
                />
            </Fade>
            <Fade in={(clicked || isFilterSeller) && inViewport} unmountOnExit>
                <div className="ListingStatusIndicator" style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-start"
                }}>
                    {isFilterSeller ?
                        <VisibilityOffIcon sx={{
                            color: "#EC407A",
                            margin: "8px",
                            filter: "drop-shadow(0 0 2px rgba(0,0,0,.5))"
                        }} /> :
                        <CheckIcon className="ListingSeenIndicator" sx={{
                            color: "white",
                            opacity: 0.6,
                            margin: "8px",
                            filter: "drop-shadow(0 0 2px rgba(0,0,0,.5))"
                        }} />
                    }
                </div>
            </Fade>
            <Fade in={showOverlay} key={item.itemId + "overlay-bottom"} unmountOnExit>
                <ImageListItemBar
                    className="ListingOverlayBottom"
                    position={'bottom'}
                    sx={{ userSelect: 'none', backgroundColor: 'transparent' }}
                    title={
                        <>
                            <div className={'hideScroll'} style={{
                                overflow: 'scroll',
                                paddingLeft: '0.5em',
                                paddingRight: '0.5em',
                                paddingTop: '0.5em',
                                paddingBottom: '0.5em',
                                lineHeight: '110%',
                                display: "flex"
                            }}>
                                <ItemViewChips item={item} sort={sort} lightweight={alwaysShow} />
                            </div>
                        </>}
                />
            </Fade>
            {(clicked || isFilterSeller) && <div
                className={`ListingStatusShade${isFilterSeller ? " ListingStatusShadeFiltered" : ""}`}
                style={{ opacity: showStatusShade ? 1 : 0 }}
            />}
            <Fade in={isFirstItemRefresh} unmountOnExit>
                <div className={"IsFirst"}
                    style={{
                        background: `linear-gradient(${firstItemPositionTopCached ? "to bottom" : "to right"}, rgba(236, 64, 122, 1), rgba(0, 0, 0, 0))`,
                        position: "absolute",
                        top: 0,
                        height: (firstItemPositionTopCached ? "15%" : "100%"),
                        width: (firstItemPositionTopCached ? "100%" : "15%")
                    }}>
                    <Fade in={firstItemPositionTopCached ? !showOverlay : true} unmountOnExit>
                        <div className={!firstItemPositionTopCached ? "flip-text" : ""}
                            style={{ display: "flex", alignItems: "center" }}>
                            {firstItemPositionTopCached ? <ArrowOutwardIcon sx={{
                                fontSize: 16,
                                marginBottom: "12px",
                                filter: "drop-shadow(0 0 2px rgba(0,0,0,.5))"
                            }} /> : <ArrowUpwardIcon sx={{
                                fontSize: 16,
                                marginBottom: "12px",
                                filter: "drop-shadow(0 0 2px rgba(0,0,0,.5))"
                            }} />}
                            <FiberNewOutlinedIcon sx={{
                                fontSize: 34,
                                marginBottom: "12px",
                                filter: "drop-shadow(0 0 2px rgba(0,0,0,.5))"
                            }} />
                        </div>
                    </Fade>
                </div>
            </Fade>
            </div>
        </ImageListItem>

    </>
    )
}

export default MemoItem;